// Talk tracks library (/talk-tracks): one row per topic with its live /
// candidate / pending versions and 30-day performance, plus "New topic" and
// "Import seed" (content/talk_tracks/seed/bundle.json, or one topic's file
// such as introduction.json).

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createTopic,
  errorText,
  getLibraryStats,
  getMeta,
  importBundle,
  listTopics,
} from "../services/talkTracks";
import type { LibraryTopicStats, TalkTrackMeta, TopicListItem } from "../types/talkTrack";
import { TopicBadges } from "../components/talkTracks/TopicBadges";
import { LOW_SAMPLE_USERS, pct, skewNote } from "../components/talkTracks/format";
import { Rate } from "../components/talkTracks/Rate";

const TOPIC_ID_PATTERN = /^[a-z0-9_-]+$/;
const STATS_DAYS = 30;

function NewTopicForm({
  categories,
  onCancel,
  onCreated,
}: {
  categories: string[];
  onCancel: () => void;
  onCreated: (topicId: string) => void;
}) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("other");
  const [introduction, setIntroduction] = useState(false);
  const [description, setDescription] = useState("");
  const [requiresSafetyScreen, setRequiresSafetyScreen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cleanId = id.trim().toLowerCase();
  const idError = cleanId && !TOPIC_ID_PATTERN.test(cleanId) ? "Lowercase letters, digits, - and _ only." : null;

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const res = await createTopic({
        id: cleanId, name: name.trim(), category, description, sortOrder: 0,
        requiresSafetyScreen: introduction ? false : requiresSafetyScreen,
        kind: introduction ? "introduction" : "playbook",
      });
      onCreated(res.topic.id);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  }

  return (
    <fieldset>
      <legend>New topic</legend>
      <div className="row-3">
        <div>
          <label htmlFor="tt-new-id">Id (slug)</label>
          <input id="tt-new-id" type="text" value={id} placeholder="manager-conflict"
            onChange={(e) => setId(e.target.value)} />
          {idError && <div className="error-text">{idError}</div>}
        </div>
        <div>
          <label htmlFor="tt-new-name">Name</label>
          <input id="tt-new-name" type="text" value={name} placeholder="Manager conflict"
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="tt-new-category">Category</label>
          <select id="tt-new-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {(categories.length ? categories : ["other"]).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <label htmlFor="tt-new-description">Description</label>
      <textarea id="tt-new-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      <label className="tt-toggle">
        <input type="checkbox" checked={introduction} onChange={(e) => setIntroduction(e.target.checked)} />
        Introduction (a new user's first conversation: life areas and a policy, never routed to)
      </label>
      {!introduction && (
        <label className="tt-toggle">
          <input type="checkbox" checked={requiresSafetyScreen} onChange={(e) => setRequiresSafetyScreen(e.target.checked)} />
          Requires at least one safety screen before publishing
        </label>
      )}
      <div className="tt-actions">
        <button className="btn btn-primary" onClick={create} disabled={busy || !cleanId || !!idError || !name.trim()}>
          {busy ? "Creating…" : "Create topic"}
        </button>
        <button className="btn" onClick={onCancel}>Cancel</button>
        {error && <span className="error-text">{error}</span>}
      </div>
    </fieldset>
  );
}

export function TalkTrackListPage() {
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const [topics, setTopics] = useState<TopicListItem[]>([]);
  const [stats, setStats] = useState<Map<string, LibraryTopicStats>>(new Map());
  const [statsError, setStatsError] = useState<string | null>(null);
  const [meta, setMeta] = useState<TalkTrackMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [notice, setNotice] = useState<{ text: string; error: boolean } | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    setStatsError(null);
    const [t, s] = await Promise.allSettled([listTopics(), getLibraryStats(STATS_DAYS)]);
    if (t.status === "fulfilled") setTopics(t.value);
    else setError(errorText(t.reason));
    if (s.status === "fulfilled") setStats(new Map(s.value.topics.map((row) => [row.topicId, row])));
    else setStatsError(errorText(s.reason));
    setLoading(false);
  }

  useEffect(() => {
    load();
    getMeta().then(setMeta).catch(() => setMeta(null));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return topics
      .filter((t) => !q || `${t.id} ${t.name} ${t.category} ${t.description}`.toLowerCase().includes(q))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }, [topics, search]);

  async function onImportFile(file: File) {
    setImporting(true);
    setNotice(null);
    try {
      let parsed: unknown;
      try {
        parsed = JSON.parse(await file.text());
      } catch {
        throw new Error(`${file.name} isn't valid JSON.`);
      }
      // A bundle, or one topic's seed file (e.g. introduction.json).
      const single = (parsed as { topic?: unknown }).topic ? [parsed] : undefined;
      const bundleTopics = (parsed as { topics?: unknown }).topics ?? single;
      if (!Array.isArray(bundleTopics) || bundleTopics.length === 0) {
        throw new Error(`${file.name} has no "topics" list — expected a seed bundle like bundle.json.`);
      }
      const res = await importBundle(bundleTopics, updateExisting);
      const updated = res.updated ?? [];
      const parts = [
        res.created.length ? `Created ${res.created.length}: ${res.created.join(", ")}.` : "Nothing new created.",
        updated.length ? `New draft for ${updated.length}: ${updated.join(", ")}.` : "",
        res.skipped.length
          ? `Skipped ${res.skipped.length} ${updateExisting ? "(open draft or unchanged)" : "existing"}: ${res.skipped.join(", ")}.`
          : "",
        res.ownersDropped?.length
          ? `No owner guide in this environment for: ${res.ownersDropped.join(", ")} (set it in topic settings).`
          : "",
      ];
      setNotice({ text: parts.filter(Boolean).join(" "), error: false });
      await load();
    } catch (e) {
      setNotice({ text: `Import failed: ${errorText(e)}`, error: true });
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  return (
    <div className="list-page">
      <div className="list-toolbar">
        <h1>Talk tracks</h1>
        <input
          className="search-box"
          type="search"
          aria-label="Search topics"
          placeholder="Search id, name, category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="spacer" />
        <button className="btn" onClick={load}>↻ Refresh</button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          hidden
          aria-label="Seed bundle file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onImportFile(file);
          }}
        />
        <label className="muted" title="Existing topics whose content changed get the seed as a new draft; published versions are untouched">
          <input
            type="checkbox"
            checked={updateExisting}
            onChange={(e) => setUpdateExisting(e.target.checked)}
          />{" "}
          Update existing as drafts
        </label>
        <button className="btn" onClick={() => fileInput.current?.click()} disabled={importing}
          title="Create topics + draft v1 from a seed bundle (existing topics are skipped unless 'Update existing as drafts' is on)">
          {importing ? "Importing…" : "Import seed"}
        </button>
        <button className="btn btn-primary" onClick={() => setCreating(true)} disabled={creating}>+ New topic</button>
      </div>

      <div className="callout tt-callout">
        Every guide serves every published topic: a per-turn director picks the topic from what the user
        says and steers the guide with its playbook. Drafts publish once their checks pass, and a
        candidate published to part of the users is A/B-tested against live.
      </div>
      {meta && !meta.enabled && (
        <div className="callout tt-callout">
          Talk tracks are switched off on this server (TALK_TRACKS_ENABLED) — nothing is served to users.
        </div>
      )}
      {notice && <div className={`callout tt-callout${notice.error ? " tt-callout-error" : ""}`}>{notice.text}</div>}

      {creating && (
        <NewTopicForm
          categories={meta?.categories ?? []}
          onCancel={() => setCreating(false)}
          onCreated={(topicId) => navigate(`/talk-tracks/${encodeURIComponent(topicId)}`)}
        />
      )}

      {loading ? (
        <div className="state-box">Loading talk tracks…</div>
      ) : error ? (
        <div className="state-box error">{error}</div>
      ) : (
        <>
          <div className="result-count">
            {filtered.length} of {topics.length} topic{topics.length === 1 ? "" : "s"} · performance over the last{" "}
            {STATS_DAYS} days{statsError ? ` (unavailable: ${statsError})` : ""}
          </div>
          {filtered.length === 0 ? (
            <div className="state-box">
              {topics.length === 0
                ? "No talk tracks yet. Import the seed bundle (content/talk_tracks/seed/bundle.json) or create a topic."
                : "No topics match your search."}
            </div>
          ) : (
            <div className="table-scroll">
              <table className="guide-table">
                <thead>
                  <tr>
                    <th className="no-sort">Topic</th>
                    <th className="no-sort">Category</th>
                    <th className="no-sort">Status</th>
                    <th className="no-sort" title="Reconnects count as one conversation">Conversations</th>
                    <th className="no-sort">Users</th>
                    <th className="no-sort">Answered</th>
                    <th className="no-sort">Drop-off</th>
                    <th className="no-sort">Offer acc.</th>
                    <th className="no-sort">Eval</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => {
                    const s = stats.get(t.id);
                    return (
                      <tr key={t.id} className="guide-row" onClick={() => navigate(`/talk-tracks/${encodeURIComponent(t.id)}`)}>
                        <td>
                          <div className="guide-name">
                            <div>
                              <div className="name">{t.name}</div>
                              <div className="role">{t.description || t.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="voice-cell">
                          {t.kind === "introduction" ? <span className="badge">introduction</span> : t.category}
                        </td>
                        <td>
                          <TopicBadges topic={t} draft={t.draftVersion} inReview={t.inReviewVersion}
                            approved={t.approvedVersion} />
                        </td>
                        <td className="voice-cell" title={s ? `${s.calls} connections` : undefined}>
                          {s ? s.conversations : "—"}
                        </td>
                        <td className="voice-cell">
                          {s ? s.users : "—"}
                          {s?.lowSample && (
                            <span className="badge low-sample"
                              title={`Fewer than ${LOW_SAMPLE_USERS} users: the ranges are wide`}>
                              low n
                            </span>
                          )}
                          {skewNote(s?.topUserShare) && (
                            <span className="badge skew" title="Rates weigh each user equally, but most traffic is one person">
                              {skewNote(s?.topUserShare)}
                            </span>
                          )}
                        </td>
                        <td className="voice-cell" title="Each user weighs the same">
                          <Rate value={s?.answerRate} range={s?.ci?.answerRate} />
                        </td>
                        <td className="voice-cell"><Rate value={s?.dropOffRate} range={s?.ci?.dropOffRate} /></td>
                        <td className="voice-cell"><Rate value={s?.offerAcceptance} range={s?.ci?.offerAcceptance} /></td>
                        <td className="voice-cell">{pct(s?.evalScore)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
